import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import { BsHourglassSplit, BsPinAngleFill } from "react-icons/bs";

import "../StayUpToDateComponent/StayUpToDate.css";
import "./ShopCardAlignment.css";

import {
  getMediaSrc,
  redirectToUrl,
  openOutboundUrl,
} from "../../Utils/StayUpToDate/RenderComponent";
import ProductName from "../ProductName/ProductName";
import ProductShopButton from "../ProductName/ProductShopButton";
import IndividualYoutubePostTag from "../StayUpToDateComponent/StayUpToDateIndividuelComponent/IndividualYoutubePostTag";
import { NoMediaPost } from "../../Screens/DashboardView/LandingPageCarousels/CommonComponents/NoMediaPost";
import { preserveBrokenImageBox } from "../../Utils/imageUtils";
import { resolveFeedCardImage } from "../../Utils/media/feedCardImage";
import { useTemplateMediaReveal } from "../../Screens/DashboardView/MasterTemplateEditor/useTemplateMediaReveal";
import "../../Screens/DashboardView/MasterTemplateEditor/templateMediaReveal.css";
import ProductBankImageFrame from "../ProductBank/ProductBankImageFrame";
import {
  shouldUseProductImageFrame,
} from "../../Utils/productImage/productImageDisplay";
import {
  hasShoppableProductStill,
  isLikelyVideoUrl,
  stillImageCandidatesFromPost,
} from "../../Utils/media/feedMedia";

const IndividualImagePostTag = React.memo((props: any) => {
  const {
    data,
    brand_color,
    title,
    isLinks,
    type,
    isYoutube,
    isCarousel,
    media_url,
    contentShape,
    handleRedirect,
    from,
    designTokens,
    pinnedPostIds = [],
    isDashboard,
    mediaIndex,
    forceProductFrame = false,
  } = props;

  const isPinned = pinnedPostIds?.includes(String(data?.post_id || data?.table_id));
  const isTemplateSite = from === "website-template";
  const suppressProductStill = Boolean(props.suppressProductStill);
  const useProductFrame =
    !suppressProductStill &&
    (Boolean(forceProductFrame) || shouldUseProductImageFrame(type, data));
  const isSquare = contentShape === "sq";

  const redirectUrl: any = redirectToUrl(data, isLinks);
  const permalinkUrl: any = data?.permalink || redirectUrl;

  // Prefer durable stills; fall through candidates when IG CDN expires.
  const stillCandidates = useMemo(() => {
    const preferProduct =
      useProductFrame ||
      (!suppressProductStill && hasShoppableProductStill(data, isLinks));
    const ranked = stillImageCandidatesFromPost(
      {
        ...data,
        media_url: data?.media_url || media_url,
      },
      { preferProductImage: preferProduct },
    );
    const extras: string[] = [];
    if (isYoutube && data?.media_url && !isLikelyVideoUrl(data.media_url)) {
      extras.push(String(data.media_url));
    }
    if (!preferProduct) {
      const primary = getMediaSrc(type, data, isCarousel, media_url);
      if (primary) extras.push(primary);
    }
    const seen = new Set(ranked);
    const out = [...ranked];
    for (const extra of extras) {
      if (extra && !seen.has(extra) && !isLikelyVideoUrl(extra)) {
        seen.add(extra);
        out.push(extra);
      }
    }
    return out;
  }, [
    data,
    media_url,
    isYoutube,
    useProductFrame,
    type,
    isCarousel,
    isLinks,
    suppressProductStill,
  ]);

  const stillKey = stillCandidates.join("\0");
  const [srcIndex, setSrcIndex] = useState(0);
  useEffect(() => {
    setSrcIndex(0);
  }, [stillKey]);

  const resolvedImageSrc = resolveFeedCardImage(
    stillCandidates[srcIndex] || "",
    !isTemplateSite,
  ).src;
  const {
    setRef,
    activeSrc,
    onLoad,
    onError: revealOnError,
    wrapperClassName: revealClass,
  } = useTemplateMediaReveal({
    enabled: isTemplateSite,
    src: resolvedImageSrc,
    index: typeof mediaIndex === "number" ? mediaIndex : undefined,
    priority: typeof mediaIndex === "number" ? mediaIndex < 6 : false,
  });

  const handleImageError = (event: { currentTarget: HTMLImageElement }) => {
    if (srcIndex + 1 < stillCandidates.length) {
      setSrcIndex((i) => i + 1);
      return;
    }
    if (isTemplateSite) {
      revealOnError?.(event as any);
      return;
    }
    preserveBrokenImageBox(event);
  };

  const openUrlWithTracking = (targetUrl: string | null | undefined) => {
    if (!targetUrl) return;

    handleRedirect(
      data?.post_id || data?.table_id,
      getMediaSrc(type, data, isCarousel, media_url),
      data?.platform,
      type === "content" || type === "query-content"
        ? data?.content_title || data?.product_name
        : data?.shop_title || data?.product_name || data?.table_name,
      title,
      isLinks,
      from
    );

    openOutboundUrl(targetUrl);
  };

  const trackShopClick = () => {
    // Shop CTA must always track as shop — never inherit carousel isLinks=false
    // (content / query-content individual pages were dropping item shop counts).
    handleRedirect(
      data?.post_id || data?.table_id,
      getMediaSrc(type, data, isCarousel, media_url),
      data?.platform,
      type === "content" || type === "query-content"
        ? data?.content_title || data?.product_name
        : data?.shop_title || data?.product_name || data?.table_name,
      title,
      true,
      from
    );
  };

  const getWrapperClassName = () => {
    const baseClass = "creator-feed-image-wrapper-links";
    const classes = [baseClass];
    if (contentShape == "sq") classes.push("square-wrapper");
    else if (isYoutube) classes.push("youtube-feed-wrapper");
    if (revealClass) classes.push(revealClass);
    return classes.join(" ");
  };

  const getImageClassName = () => {
    const classes = ["card-image-individual"];
    if (contentShape == "sq") classes.push("square-image");
    else if (isYoutube) classes.push("youtube-feed-height");
    return classes.join(" ");
  };

  // YouTube without product_image: render specialized tag
  if (data?.platform === "youtube" && !data?.product_image) {
    const redirectUrlYt: any = redirectToUrl(data, isLinks);
    const handleClickYt = (e: React.MouseEvent) => {
      e.preventDefault();
      if (!redirectUrlYt) return;
      handleRedirect(
        data?.post_id || data?.table_id,
        data?.media_url,
        data?.platform,
        type === "content" || type === "query-content"
          ? data?.content_title || data?.product_name
          : data?.shop_title || data?.product_name || data?.table_name,
        title,
        isLinks,
        from
      );
      openOutboundUrl(redirectUrlYt);
    };
    return (
      <IndividualYoutubePostTag
        link={data}
        handleRedirect={handleRedirect}
        bgColor={designTokens?.primaryColor || brand_color}
        contentShape={contentShape}
        handleClick={handleClickYt}
        redirectUrl={redirectUrlYt}
        pinnedPostIds={pinnedPostIds}
        from={from}
        isDashboard={isDashboard}
        designTokens={designTokens}
      />
    );
  }

  const renderPinnedBadge = () =>
    isPinned ? (
      <div className="pinned-badge" aria-label="Pinned post" style={{ right: "4px" }}>
        <BsPinAngleFill />
      </div>
    ) : null;

  const renderNoMedia = () => (
    <div
      className="creator-post-stack"
      style={{ position: "relative" }}
    >
      <div
        className={getWrapperClassName()}
        style={{
          textAlign: "center",
          height: contentShape == "sq" ? "auto" : "100%",
          borderRadius: "1rem",
          color: "#000",
          position: "relative",
        }}
      >
        <div
          onClick={() => openUrlWithTracking(permalinkUrl)}
          style={{ cursor: "pointer" }}
        >
          {renderPinnedBadge()}
          <NoMediaPost
            post={data}
            type={type}
            label={
              data?.platform === "newsletter"
                ? data?.caption || data?.content_title || "Newsletter"
                : data?.platform === "linkedin"
                  ? "LinkedIn Post"
                  : data?.platform === "twitter" ||
                      String(data?.platform || "").toLowerCase() === "x"
                    ? "X Post"
                    : "Post"
            }
            className={`newsletter-media-carousel ${getImageClassName()} no-media-url`}
            showDate={!!data?.post_date}
            showMeta={false}
            designTokens={designTokens}
          />
        </div>
      </div>
      <ProductName type={type} post={data} designTokens={designTokens} />
      <ProductShopButton
        isLinks={isLinks}
        isIndividual={true}
        type={type}
        post={data}
        onBeforeNavigate={trackShopClick}
        designTokens={designTokens}
      />
    </div>
  );

  const renderProductMedia = (src: string) => {
    if (!src) return null;
    return (
    <div
      className={`individual-product-media-wrp${isSquare ? " individual-product-media-wrp--square" : ""}`}
      style={{ marginBottom: "0%" }}
    >
      <ProductBankImageFrame
        src={resolveFeedCardImage(src, true).src}
        alt={data?.product_name || data?.shop_title || "Product"}
        fill
        shape={contentShape}
        square={isSquare}
        onError={handleImageError}
      />
    </div>
    );
  };

  const renderInstagram = () => (
    <div
      className="creator-post-stack"
      style={{ position: "relative" }}
    >
      <div
        ref={setRef as React.Ref<HTMLDivElement>}
        className={getWrapperClassName()}
        style={{ position: "relative" }}
      >
        <div
          onClick={() => openUrlWithTracking(permalinkUrl)}
          style={{ cursor: "pointer" }}
        >
          {renderPinnedBadge()}
          {useProductFrame ? (
            renderProductMedia(stillCandidates[srcIndex] || "")
          ) : activeSrc ? (
            <Card.Img
              src={activeSrc}
              className={getImageClassName()}
              loading={isTemplateSite ? "lazy" : "eager"}
              decoding="async"
              onLoad={isTemplateSite ? onLoad : undefined}
              onError={handleImageError}
              style={{
                marginBottom: "0%",
                borderColor: designTokens?.primaryColor || brand_color,
              }}
            />
          ) : null}
        </div>
      </div>
      <ProductName type={type} post={data} designTokens={designTokens} />
      <ProductShopButton
        isLinks={isLinks}
        isIndividual={true}
        type={type}
        post={data}
        onBeforeNavigate={trackShopClick}
        designTokens={designTokens}
      />
    </div>
  );

  const renderNotAvailable = () => (
    <div className={"creator-feed-card-links not-available"}>
      <BsHourglassSplit className="hourglassSplit" />
      <p>Work in progress: This content will be available in a few minutes.</p>
    </div>
  );

  const renderDefaultMedia = () => (
    <div
      className="creator-post-stack"
      style={{ position: "relative" }}
    >
      <div
        ref={setRef as React.Ref<HTMLDivElement>}
        className={getWrapperClassName()}
        style={{ position: "relative" }}
      >
        <div
          onClick={() => openUrlWithTracking(permalinkUrl)}
          style={{ cursor: "pointer" }}
        >
          {renderPinnedBadge()}
          {useProductFrame ? (
            renderProductMedia(stillCandidates[srcIndex] || "")
          ) : activeSrc ? (
            <Card.Img
              src={activeSrc}
              className={getImageClassName()}
              loading={isTemplateSite ? "lazy" : "eager"}
              decoding="async"
              onLoad={isTemplateSite ? onLoad : undefined}
              onError={handleImageError}
              style={{
                marginBottom: "0%",
              }}
            />
          ) : null}
        </div>
      </div>
      <ProductName type={type} post={data} designTokens={designTokens} />
      <ProductShopButton
        isLinks={isLinks}
        isIndividual={true}
        type={type}
        post={data}
        onBeforeNavigate={trackShopClick}
        designTokens={designTokens}
      />
    </div>
  );

  const isTextPlatform =
    data?.platform === "newsletter" ||
    data?.platform === "linkedin" ||
    data?.platform === "twitter" ||
    String(data?.platform || "").toLowerCase() === "x";
  const hasStill = stillCandidates.length > 0;

  return (
    <Card style={{ border: "none" }}>
      {!hasStill && isTextPlatform
        ? renderNoMedia()
        : !hasStill && data?.platform === "instagram"
          ? renderInstagram()
          : !hasStill
            ? renderNotAvailable()
            : renderDefaultMedia()}
    </Card>
  );
});

export default IndividualImagePostTag;
